import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MemberHeader } from '../common/MemberHeader';
import { MemberBreadcrumb } from '../common/MemberBreadcrumb';
import { AppFooter } from '../common/AppFooter';
import { LicensesGroupedList } from '../common/LicensesGroupedList';
import { MemberMailHistory } from '../common/MemberMailHistory';
import { ProfileCompletionGate } from '../common/ProfileCompletionGate';
import { bookingApi, licenseApi, authApi, renewalApi, emailApi } from '../../lib/api';
import { formatCurrency, isSeminarOver } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import CancellationRequestDialog from '../common/CancellationRequestDialog';
import {
    Heart, LogOut, Calendar, MapPin, Clock, Award,
    GraduationCap, User, BookOpen, ChevronRight, ExternalLink,
    Download, FileText, Shield, AlertTriangle, Loader2, Save, Send,
    XCircle,
} from 'lucide-react';

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        let d;
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
            d = new Date(dateStr + 'T00:00:00');
        }
        if (!d || isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
}

export default function ParticipantPortalPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [renewals, setRenewals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileData, setProfileData] = useState({
        salutation: '', academic_title: '', first_name: '', last_name: '',
        phone: '',
        birth_date: '', street: '', postal_code: '', city: '', club: '',
    });

    const profileIncomplete = !user?.birth_date || !user?.street || !user?.postal_code || !user?.city;

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [bookingsRes, licensesRes, renewalsRes] = await Promise.all([
                bookingApi.getMy(),
                licenseApi.getMy(),
                renewalApi.getMyRenewals().catch(() => ({ data: [] })),
            ]);
            setBookings(bookingsRes.data);
            setLicenses(licensesRes.data);
            setRenewals(renewalsRes.data || []);
        } catch (error) {
            console.error('Fehler beim Laden:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/seminare');
    };

    const openProfileDialog = () => {
        setProfileData({
            salutation: user?.salutation || '',
            academic_title: user?.academic_title || '',
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            phone: user?.phone || '',
            birth_date: user?.birth_date || '',
            street: user?.street || '',
            postal_code: user?.postal_code || '',
            city: user?.city || '',
            club: user?.club || '',
        });
        setProfileOpen(true);
    };

    const handleProfileSave = async (e) => {
        e.preventDefault();
        if (!profileData.first_name || !profileData.last_name || !profileData.birth_date || !profileData.street || !profileData.postal_code || !profileData.city) {
            toast.error('Bitte alle Pflichtfelder ausfüllen');
            return;
        }
        setProfileSaving(true);
        try {
            const res = await authApi.updateProfile(profileData);
            const updatedUser = res.data;
            localStorage.setItem('bgpr_user', JSON.stringify(updatedUser));
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setProfileSaving(false);
        }
    };

    const upcomingBookings = bookings.filter(b => {
        if (!b.seminar) return false;
        // Anwesenheit oder abgeschlossen → immer Vergangenheit
        if (b.attended || b.status === 'completed') return false;
        // Verzweigt auf die Backend-symmetrische Hilfsfunktion (berücksichtigt Uhrzeit)
        return !isSeminarOver(b.seminar);
    });

    const pastBookings = bookings.filter(b => !upcomingBookings.includes(b));
    // Besuchte Seminare: Anwesenheit bestätigt — hier liegen Lehrgangs-
    // bescheinigung und (sofern bezahlt) Zahlungsbeleg zum Download bereit.
    const attendedBookings = pastBookings.filter(b => b.attended);
    // Sonstige vergangene Buchungen: ausgelaufen ohne Anwesenheit (No-Show,
    // storniert, oder noch nicht als anwesend markiert).
    const otherPastBookings = pastBookings.filter(b => !b.attended);

    const isImpersonating = sessionStorage.getItem('bgpr_impersonating') === 'true';

    return (
        <div className="min-h-screen bg-gray-50">
            <ProfileCompletionGate />
            {/* Impersonation Banner */}
            {isImpersonating && (
                <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-3" data-testid="impersonation-banner">
                    <span>Admin-Ansicht als: {user?.first_name} {user?.last_name}</span>
                    <button
                        onClick={() => {
                            sessionStorage.removeItem('bgpr_token');
                            sessionStorage.removeItem('bgpr_user');
                            sessionStorage.removeItem('bgpr_impersonating');
                            window.close();
                        }}
                        className="underline hover:no-underline font-semibold"
                        data-testid="exit-impersonation"
                    >
                        Tab schließen
                    </button>
                </div>
            )}
            {/* Header */}
            <MemberHeader />
            <MemberBreadcrumb />

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                {/* Welcome */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Willkommen, {user?.first_name}!
                    </h2>
                    <p className="text-gray-500 mt-1">Hier finden Sie Ihre gebuchten Seminare und Zertifikate.</p>
                </div>

                {/* Kontaktdaten unvollständig */}
                {profileIncomplete && (
                    <div className="mb-8 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="profile-incomplete-banner">
                        <div className="p-2.5 bg-amber-100 rounded-xl shrink-0">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-amber-900">Kontaktdaten unvollständig</h3>
                            <p className="text-sm text-amber-700 mt-0.5">
                                Bitte vervollständigen Sie Ihre Kontaktdaten, damit wir Ihnen Lehrgangsbescheinigungen, Lizenzen und Rechnungen korrekt zustellen können.
                            </p>
                        </div>
                        <Button
                            onClick={() => navigate('/kontaktdaten')}
                            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                            data-testid="complete-profile-button"
                        >
                            <User className="h-4 w-4 mr-1.5" />
                            Kontaktdaten vervollständigen
                        </Button>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2.5 bg-blue-50 rounded-xl">
                                <BookOpen className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
                                <p className="text-xs text-gray-500">Buchungen gesamt</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2.5 bg-green-50 rounded-xl">
                                <Calendar className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900">{upcomingBookings.length}</p>
                                <p className="text-xs text-gray-500">Kommende Seminare</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2.5 bg-amber-50 rounded-xl">
                                <Shield className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900">
                                    {licenses.length}
                                </p>
                                <p className="text-xs text-gray-500">Lizenzen</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Upcoming Seminars */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        Kommende Seminare
                    </h3>
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                        </div>
                    ) : upcomingBookings.length === 0 ? (
                        <Card>
                            <CardContent className="p-8 text-center">
                                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-gray-500 mb-4">Sie haben noch keine kommenden Seminare gebucht.</p>
                                <Link
                                    to="/seminare"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors font-medium text-sm"
                                    data-testid="browse-seminars-link"
                                >
                                    Seminare entdecken
                                    <ChevronRight className="h-4 w-4" />
                                </Link>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {upcomingBookings.map((booking) => (
                                <BookingCard key={booking.id} booking={booking} onChanged={loadData} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Besuchte Seminare — Anwesenheit bestätigt, Bescheinigung + ggf. Zahlungsbeleg verfügbar */}
                {attendedBookings.length > 0 && (
                    <div className="mb-8" data-testid="attended-seminars-section">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                            <Award className="h-5 w-5 text-primary" />
                            Besuchte Seminare
                            <span className="text-sm font-normal text-muted-foreground">({attendedBookings.length})</span>
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Lade hier deine Lehrgangsbescheinigungen und – sofern eine Zahlung erfasst wurde – deine Zahlungsbelege (Quittung) herunter.
                        </p>
                        <div className="space-y-3">
                            {attendedBookings.map((booking) => (
                                <BookingCard key={booking.id} booking={booking} isPast />
                            ))}
                        </div>
                    </div>
                )}

                {/* Übrige vergangene Buchungen: nicht besucht, storniert, no-show */}
                {otherPastBookings.length > 0 && (
                    <div className="mb-8" data-testid="other-past-bookings-section">
                        <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            Sonstige vergangene Buchungen
                            <span className="text-sm font-normal text-muted-foreground">({otherPastBookings.length})</span>
                        </h3>
                        <div className="space-y-3">
                            {otherPastBookings.map((booking) => (
                                <BookingCard key={booking.id} booking={booking} isPast />
                            ))}
                        </div>
                    </div>
                )}

                {/* Lizenzen */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Shield className="h-5 w-5 text-primary" />
                            Meine Lizenzen
                        </h3>
                        {licenses.length > 0 && (
                            <Button
                                onClick={() => navigate('/lizenzverlaengerung')}
                                className="bg-primary hover:bg-primary/80"
                                size="sm"
                                data-testid="portal-go-to-renewal-btn"
                            >
                                <Send className="h-4 w-4 mr-1.5" />
                                Jetzt verlängern
                            </Button>
                        )}
                    </div>
                    {licenses.length === 0 ? (
                        <Card>
                            <CardContent className="p-8 text-center">
                                <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-gray-500">Es liegen noch keine Lizenzen für Sie vor.</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Über <Link to="/lizenzverlaengerung" className="text-primary underline hover:no-underline">„Meine Lizenz verlängern"</Link> können Sie Ihre bestehende Lizenz hochladen.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <LicensesGroupedList licenses={licenses} renewals={renewals} />
                    )}
                </div>

                {/* E-Mail-Verlauf (Self-Service – DSGVO-Auskunft) */}
                <div className="mt-2">
                    <MemberMailHistory
                        loader={(params) => emailApi.myLogs(params)}
                        showError={false}
                        title="Meine E-Mail-Historie"
                        testIdPrefix="my-mail"
                    />
                </div>


                {/* Browse Seminars CTA */}
                <Card className="bg-secondary border-0">
                    <CardContent className="p-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-white font-semibold mb-1">Weitere Seminare entdecken</h3>
                            <p className="text-gray-400 text-sm">Finden und buchen Sie Ihre nächste Fortbildung</p>
                        </div>
                        <Link
                            to="/seminare"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors font-medium text-sm whitespace-nowrap"
                        >
                            Alle Seminare
                            <ChevronRight className="h-4 w-4" />
                        </Link>
                    </CardContent>
                </Card>

                {/* Kontaktdaten-Dialog */}
                <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
                    <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="profile-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-heading flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Meine Kontaktdaten
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleProfileSave}>
                            <p className="text-sm text-gray-500 mb-4">
                                Diese Angaben werden für Ihre Lehrgangsbescheinigungen, Lizenzen und Rechnungen verwendet.
                                Bitte halten Sie sie aktuell (z.B. bei Namensänderung oder Umzug).
                            </p>
                            <div className="space-y-4">
                                {/* Persönliche Daten */}
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Person</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <Label htmlFor="profile_salutation" className="text-xs">Anrede</Label>
                                            <Select value={profileData.salutation} onValueChange={(v) => setProfileData({ ...profileData, salutation: v })}>
                                                <SelectTrigger id="profile_salutation" data-testid="profile-salutation"><SelectValue placeholder="Bitte wählen..." /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Herr">Herr</SelectItem>
                                                    <SelectItem value="Frau">Frau</SelectItem>
                                                    <SelectItem value="Divers">Divers</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="profile_title" className="text-xs">Titel (optional)</Label>
                                            <Input id="profile_title" placeholder="Dr., Prof., …" value={profileData.academic_title} onChange={(e) => setProfileData({ ...profileData, academic_title: e.target.value })} data-testid="profile-title" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="profile_first_name" className="text-xs">Vorname <span className="text-red-500">*</span></Label>
                                            <Input id="profile_first_name" value={profileData.first_name} onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })} required data-testid="profile-first-name" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="profile_last_name" className="text-xs">Nachname <span className="text-red-500">*</span></Label>
                                            <Input id="profile_last_name" value={profileData.last_name} onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })} required data-testid="profile-last-name" />
                                        </div>
                                        <div className="space-y-1 col-span-2">
                                            <Label htmlFor="profile_birth_date" className="text-xs">Geburtsdatum <span className="text-red-500">*</span></Label>
                                            <Input id="profile_birth_date" type="date" value={profileData.birth_date} onChange={(e) => setProfileData({ ...profileData, birth_date: e.target.value })} required data-testid="profile-birth-date" />
                                        </div>
                                    </div>
                                </div>

                                {/* Kontakt */}
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Kontakt</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1 col-span-2">
                                            <Label htmlFor="profile_email" className="text-xs">E-Mail</Label>
                                            <Input id="profile_email" type="email" value={user?.email || ''} disabled data-testid="profile-email" />
                                            <p className="text-xs text-muted-foreground">Zum Ändern bitte die Geschäftsstelle kontaktieren.</p>
                                        </div>
                                        <div className="space-y-1 col-span-2">
                                            <Label htmlFor="profile_phone" className="text-xs">Telefon (optional)</Label>
                                            <Input id="profile_phone" placeholder="+49 30 123 456" value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} data-testid="profile-phone" />
                                        </div>
                                    </div>
                                </div>

                                {/* Adresse */}
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Anschrift</h4>
                                    <div className="space-y-3">
                                        <div className="space-y-1">
                                            <Label htmlFor="profile_street" className="text-xs">Straße und Hausnummer <span className="text-red-500">*</span></Label>
                                            <Input id="profile_street" placeholder="Musterstraße 1" value={profileData.street} onChange={(e) => setProfileData({ ...profileData, street: e.target.value })} required data-testid="profile-street" />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="space-y-1">
                                                <Label htmlFor="profile_postal_code" className="text-xs">PLZ <span className="text-red-500">*</span></Label>
                                                <Input id="profile_postal_code" placeholder="10115" value={profileData.postal_code} onChange={(e) => setProfileData({ ...profileData, postal_code: e.target.value })} required data-testid="profile-postal-code" />
                                            </div>
                                            <div className="col-span-2 space-y-1">
                                                <Label htmlFor="profile_city" className="text-xs">Ort <span className="text-red-500">*</span></Label>
                                                <Input id="profile_city" placeholder="Berlin" value={profileData.city} onChange={(e) => setProfileData({ ...profileData, city: e.target.value })} required data-testid="profile-city" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Verein */}
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Verein / Organisation</h4>
                                    <div className="space-y-1">
                                        <Label htmlFor="profile_club" className="text-xs">Verein <span className="font-normal text-gray-400">(optional)</span></Label>
                                        <Input id="profile_club" placeholder="z.B. Herzsportgruppe Mitte e.V." value={profileData.club} onChange={(e) => setProfileData({ ...profileData, club: e.target.value })} data-testid="profile-club" />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter className="mt-6">
                                <Button type="button" variant="outline" onClick={() => setProfileOpen(false)}>
                                    Abbrechen
                                </Button>
                                <Button type="submit" disabled={profileSaving} data-testid="profile-save-button">
                                    {profileSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                    Speichern
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

            </main>
            <AppFooter />
        </div>
    );
}

function BookingCard({ booking, isPast = false, onChanged }) {
    const s = booking.seminar;
    const [downloading, setDownloading] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    if (!s) return null;

    const isCancelRequested = booking.cancellation_status === 'requested';
    const isCourseCancelled = !!booking.cancelled_due_to_course_cancellation;
    const rebookToken = booking.rebook_token;
    const rebookStatus = booking.rebook_choice_status;
    const statusMap = {
        confirmed: { label: 'Bestätigt', cls: 'bg-green-50 text-green-700' },
        pending: { label: 'Ausstehend', cls: 'bg-amber-50 text-amber-700' },
        cancelled: { label: 'Storniert', cls: 'bg-red-50 text-red-700' },
        completed: { label: 'Abgeschlossen', cls: 'bg-blue-50 text-blue-700' },
        waiting_list: { label: 'Warteliste', cls: 'bg-slate-100 text-slate-700' },
        pending_confirmation: { label: 'Wartelisten-Annahme läuft', cls: 'bg-amber-50 text-amber-700' },
    };
    const status = isCourseCancelled
        ? { label: 'Kurs abgesagt', cls: 'bg-red-50 text-red-700 border border-red-200' }
        : isCancelRequested
            ? { label: 'Stornierung beantragt', cls: 'bg-amber-50 text-amber-800 border border-amber-200' }
            : (statusMap[booking.status] || statusMap.pending);
    const canCancel = !isPast
        && !isCancelRequested
        && ['confirmed', 'pending', 'waiting_list'].includes(booking.status);
    const isFree = Number(booking.final_price || 0) <= 0;

    const handleFreeCancel = async () => {
        if (!window.confirm('Möchten Sie sich von diesem kostenfreien Seminar wirklich abmelden?')) return;
        try {
            await bookingApi.cancelSelf(booking.id);
            toast.success('Buchung wurde storniert');
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Stornierung fehlgeschlagen');
        }
    };

    const isAusbildung = s.event_category === 'ausbildung';
    const certLabel = isAusbildung ? 'Lizenz' : 'Bescheinigung';

    const handleDownloadCert = async () => {
        setDownloading(true);
        try {
            const res = await bookingApi.downloadCertificate(booking.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${certLabel}_${s.title.substring(0, 20)}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
            toast.success(`${certLabel} heruntergeladen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || `${certLabel} konnte nicht erstellt werden`);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadReceipt = async () => {
        setDownloadingReceipt(true);
        try {
            const res = await bookingApi.downloadReceipt(booking.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Zahlungsbeleg_${booking.receipt_invoice_number || booking.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
            toast.success('Zahlungsbeleg heruntergeladen');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Beleg konnte nicht heruntergeladen werden');
        } finally {
            setDownloadingReceipt(false);
        }
    };

    return (
        <Card className={`hover:shadow-md transition-shadow ${isCourseCancelled ? 'border-red-200 bg-red-50/30' : ''}`} data-testid={`booking-card-${booking.id}`}>
            <CardContent className="p-4 sm:p-5">
                {isCourseCancelled && (
                    <div className="mb-3 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-900" data-testid={`booking-cancelled-banner-${booking.id}`}>
                        <div className="font-semibold mb-0.5">Dieser Kurs wurde von der Geschäftsstelle abgesagt.</div>
                        {booking.cancellation_reason_text && (
                            <div className="text-xs mb-1"><span className="font-medium">Grund:</span> {booking.cancellation_reason_text}</div>
                        )}
                        {rebookStatus === 'pending' && rebookToken && (
                            <a
                                href={`/umbuchung/${rebookToken}`}
                                className="inline-block mt-1 text-xs font-semibold text-red-900 underline hover:text-red-700"
                                data-testid={`booking-rebook-link-${booking.id}`}
                            >
                                Umbuchung oder Erstattung wählen &rarr;
                            </a>
                        )}
                        {rebookStatus === 'rebooked' && (
                            <div className="text-xs text-emerald-800 mt-1">✔ Sie wurden umgebucht — neuen Termin in Ihrer Buchungsliste einsehen.</div>
                        )}
                        {rebookStatus === 'refunded' && (
                            <div className="text-xs text-emerald-800 mt-1">✔ Erstattung wurde erfasst und wird zeitnah bearbeitet.</div>
                        )}
                        {booking.rebooked_to_booking_id && !rebookStatus && (
                            <div className="text-xs text-emerald-800 mt-1">✔ Automatische Umbuchung erfolgt — den neuen Termin sehen Sie in Ihrer Buchungsliste.</div>
                        )}
                    </div>
                )}                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900">{s.title}</h4>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}>
                                {status.label}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                {formatDate(s.date)}
                            </span>
                            <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {s.time_start} - {s.time_end}
                            </span>
                            <span className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {s.location}
                            </span>
                            <span className="flex items-center gap-1">
                                <GraduationCap className="h-3.5 w-3.5" />
                                {s.ue_count} UE
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {booking.receipt_available && (
                            <button
                                onClick={handleDownloadReceipt}
                                disabled={downloadingReceipt}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-xs font-medium hover:bg-green-200 transition-colors"
                                data-testid={`download-receipt-${booking.id}`}
                                title="Zahlungsbeleg (Quittung) herunterladen"
                            >
                                {downloadingReceipt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                Zahlungsbeleg
                            </button>
                        )}
                        {isPast && booking.attended && s.organizer_type !== 'external' && (
                            <button
                                onClick={handleDownloadCert}
                                disabled={downloading}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors"
                                data-testid={`download-cert-${booking.id}`}
                            >
                                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                {certLabel}
                            </button>
                        )}
                        {isPast && booking.attended && s.organizer_type !== 'external' && !booking.certificate_generated && (
                            <span className="text-xs text-muted-foreground">(wird beim Download erstellt)</span>
                        )}
                        {isPast && booking.attended && s.organizer_type === 'external' && (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-800 rounded-lg text-xs font-medium" data-testid={`external-cert-hint-${booking.id}`}>
                                Bescheinigung wird vom externen Anbieter ausgestellt
                            </span>
                        )}
                        {canCancel && (
                            <button
                                onClick={() => (isFree ? handleFreeCancel() : setCancelOpen(true))}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 text-rose-700 rounded-lg text-xs font-medium hover:bg-rose-100 transition-colors"
                                data-testid={`cancel-booking-${booking.id}`}
                                title={isFree ? 'Abmelden' : 'Buchung stornieren'}
                            >
                                <XCircle className="h-3.5 w-3.5" />
                                {isFree ? 'Abmelden' : 'Stornieren'}
                            </button>
                        )}
                        <Link
                            to="#"
                            onClick={(e) => { e.preventDefault(); setDetailsOpen(true); }}
                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
                            data-testid={`booking-details-${booking.id}`}
                        >
                            Details
                            <ChevronRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
                <SeminarDetailsDialog seminar={s} booking={booking} open={detailsOpen} onOpenChange={setDetailsOpen} />
                <CancellationRequestDialog
                    booking={booking}
                    open={cancelOpen}
                    onOpenChange={setCancelOpen}
                    onSubmitted={onChanged}
                />
            </CardContent>
        </Card>
    );
}

function SeminarDetailsDialog({ seminar, booking, open, onOpenChange }) {
    if (!seminar) return null;
    const dates = seminar.dates && seminar.dates.length ? seminar.dates : [];
    const statusLabel = {
        confirmed: 'Bestätigt',
        pending: 'Ausstehend',
        cancelled: 'Storniert',
        completed: 'Abgeschlossen',
    }[booking?.status] || '–';
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="portal-seminar-details">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">{seminar.title}</DialogTitle>
                    {seminar.subtitle && <p className="text-sm text-muted-foreground mt-1">{seminar.subtitle}</p>}
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {seminar.course_number && (
                            <div><span className="text-muted-foreground">Kursnummer:</span> <span className="font-medium">{seminar.course_number}</span></div>
                        )}
                        <div><span className="text-muted-foreground">Ort:</span> <span className="font-medium">{seminar.location || '–'}</span></div>
                        <div><span className="text-muted-foreground">Zeit:</span> <span className="font-medium">{seminar.time_start} – {seminar.time_end} Uhr</span></div>
                        <div><span className="text-muted-foreground">UE:</span> <span className="font-medium">{seminar.ue_count || 0} UE</span></div>
                        {seminar.event_format && (
                            <div><span className="text-muted-foreground">Format:</span> <span className="font-medium capitalize">{seminar.event_format}</span></div>
                        )}
                        {seminar.event_category && (
                            <div><span className="text-muted-foreground">Art:</span> <span className="font-medium capitalize">{seminar.event_category}</span></div>
                        )}
                        <div><span className="text-muted-foreground">Ihr Status:</span> <span className="font-medium">{statusLabel}</span></div>
                        {booking?.attended !== undefined && (
                            <div><span className="text-muted-foreground">Teilnahme:</span> <span className="font-medium">{booking.attended ? 'bestätigt' : 'offen'}</span></div>
                        )}
                    </div>
                    {dates.length > 0 && (
                        <div>
                            <div className="text-sm text-muted-foreground mb-1">Termine:</div>
                            <ul className="text-sm space-y-0.5">
                                {dates.map((d, i) => (
                                    <li key={i} className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-primary" />{formatDate(d.date)}{d.time_start ? ` · ${d.time_start}–${d.time_end}` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {seminar.description && (
                        <div>
                            <div className="text-sm text-muted-foreground mb-1">Beschreibung:</div>
                            <div
                                className="legal-html text-sm"
                                dangerouslySetInnerHTML={{ __html: seminar.description }}
                            />
                        </div>
                    )}
                    {seminar.certificate_content && (
                        <div>
                            <div className="text-sm text-muted-foreground mb-1">Inhalte der Bescheinigung:</div>
                            <div
                                className="legal-html text-sm"
                                dangerouslySetInnerHTML={{ __html: seminar.certificate_content }}
                            />
                        </div>
                    )}
                    {seminar.external_url && (
                        <a href={seminar.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                            <ExternalLink className="h-3.5 w-3.5" />
                            Externe Info-Seite öffnen
                        </a>
                    )}
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="portal-seminar-details-close">Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


